import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ShieldCheck, Search, Users, ArrowRight, CheckCircle, 
  Truck, Package, MapPin, Shield, AlertTriangle, UserCheck, Eye
} from 'lucide-react';

// HaulLogix Shield Logo Component - Exact Match to Brand Logo
const HaulLogixLogo = () => (
  <div className="flex flex-col items-center">
    <svg width="100" height="112" viewBox="0 0 100 110" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        {/* Shield border gradient - blue to light blue/silver */}
        <linearGradient id="heroShieldBorder" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1E3A5F" />
          <stop offset="40%" stopColor="#3B82F6" />
          <stop offset="70%" stopColor="#60A5FA" />
          <stop offset="100%" stopColor="#94A3B8" />
        </linearGradient>
        {/* Inner shield background - dark navy */}
        <linearGradient id="heroInnerShield" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1E3A5F" />
          <stop offset="100%" stopColor="#0F172A" />
        </linearGradient>
        {/* Blue bar gradient - left side */}
        <linearGradient id="heroBlueBar" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#38BDF8" />
          <stop offset="30%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#1D4ED8" />
        </linearGradient>
        {/* Green bar gradient - right side */}
        <linearGradient id="heroGreenBar" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#4ADE80" />
          <stop offset="30%" stopColor="#22C55E" />
          <stop offset="100%" stopColor="#15803D" />
        </linearGradient>
        <filter id="heroGlow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      {/* Shield outer border with glow */}
      <path 
        d="M50 5L10 22V55C10 82 50 105 50 105C50 105 90 82 90 55V22L50 5Z" 
        fill="url(#heroShieldBorder)"
        filter="url(#heroGlow)"
      />
      {/* Shield inner dark area */}
      <path 
        d="M50 12L16 27V55C16 78 50 98 50 98C50 98 84 78 84 55V27L50 12Z" 
        fill="url(#heroInnerShield)"
      />
      {/* Blue vertical bar (left) - forms left of H */}
      <path d="M26 30 L26 78 L40 72 L40 36 Z" fill="url(#heroBlueBar)" />
      {/* Green vertical bar (right) - forms right of H */}
      <path d="M60 36 L60 72 L74 78 L74 30 Z" fill="url(#heroGreenBar)" />
      {/* White H crossbar connecting the bars */}
      <rect x="36" y="48" width="28" height="12" fill="#FFFFFF" opacity="0.95" />
    </svg>
    <h1 className="text-5xl font-bold tracking-tight mt-6" style={{fontFamily: 'Poppins, sans-serif'}}>
      <span className="text-white">Haul</span><span className="text-[#4ADE80]">Logix</span>
    </h1>
    <p className="text-[#94A3B8] text-sm tracking-widest mt-3 uppercase">
      Verified Auto Transport Loadboard
    </p>
  </div>
);

const LandingPage = () => {
  const features = [
    {
      icon: ShieldCheck,
      title: 'Verified Network',
      description: 'Every carrier and broker is verified. No fakes, no fraud.',
    },
    {
      icon: Search,
      title: 'Find Loads Fast',
      description: 'Browse verified listings from trusted brokers and shippers.',
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Connect with industry professionals in the Community forum.',
    },
    {
      icon: Truck,
      title: 'Real-Time Tracking',
      description: 'Track every load from posted to delivered.',
    },
  ];

  // Sample loads for preview
  const sampleLoads = [
    { origin: 'Dallas, TX', dest: 'Los Angeles, CA', vehicle: 'Enclosed', price: 3200, age: '2h', status: 'verified' },
    { origin: 'Atlanta, GA', dest: 'Cleveland, OH', vehicle: 'Reefer', price: 1100, age: '5h', status: 'assigned' },
    { origin: 'Phoenix, AZ', dest: 'Portland, OR', vehicle: 'Step Deck', price: 1450, age: '7h', status: 'verified' },
    { origin: 'Chicago, IL', dest: 'Nashville, TN', vehicle: 'Open', price: 1750, age: '8h', status: 'assigned' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A1628] via-[#0D1B2A] to-[#081424]">
      {/* Hero Section */}
      <section className="pt-28 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center animate-fade-in">
          <HaulLogixLogo />
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-[#4ADE80] to-[#22C55E] hover:from-[#86EFAC] hover:to-[#4ADE80] text-black font-semibold h-14 px-10 text-base"
                style={{boxShadow: '0 6px 24px rgba(74, 222, 128, 0.4)'}}
                data-testid="hero-get-started-btn"
              >
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/loadboard">
              <Button 
                size="lg" 
                variant="outline"
                className="h-14 px-10 text-base border-[#3B82F6] bg-[#1E3A8A]/40 text-white hover:bg-[#3B82F6]/30 font-medium"
                style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.25)'}}
                data-testid="hero-browse-loads-btn"
              >
                Browse Loads
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Preview Loadboard */}
      <section className="pb-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-[#112240] rounded-2xl border border-[#3B82F6]/20 overflow-hidden animate-fade-in" style={{boxShadow: '0 8px 40px rgba(59, 130, 246, 0.15)'}}>
            {/* Mini Header */}
            <div className="flex items-center gap-4 px-6 py-4 border-b border-[#3B82F6]/20 bg-[#162A4A]">
              <svg width="24" height="27" viewBox="0 0 100 110" fill="none">
                <defs>
                  <linearGradient id="miniShieldBorder" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1E3A5F" />
                    <stop offset="50%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#94A3B8" />
                  </linearGradient>
                  <linearGradient id="miniInnerShield" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#1E3A5F" />
                    <stop offset="100%" stopColor="#0F172A" />
                  </linearGradient>
                  <linearGradient id="miniBlueBar" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#38BDF8" />
                    <stop offset="100%" stopColor="#1D4ED8" />
                  </linearGradient>
                  <linearGradient id="miniGreenBar" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#4ADE80" />
                    <stop offset="100%" stopColor="#15803D" />
                  </linearGradient>
                </defs>
                <path d="M50 5L10 22V55C10 82 50 105 50 105C50 105 90 82 90 55V22L50 5Z" fill="url(#miniShieldBorder)"/>
                <path d="M50 12L16 27V55C16 78 50 98 50 98C50 98 84 78 84 55V27L50 12Z" fill="url(#miniInnerShield)"/>
                <path d="M26 30 L26 78 L40 72 L40 36 Z" fill="url(#miniBlueBar)" />
                <path d="M60 36 L60 72 L74 78 L74 30 Z" fill="url(#miniGreenBar)" />
                <rect x="36" y="48" width="28" height="12" fill="#FFFFFF" opacity="0.95" />
              </svg>
              <span className="text-base font-semibold"><span className="text-white">Haul</span><span className="text-[#4ADE80]">Logix</span></span>
              <span className="text-sm text-gray-500 ml-2">Load Board</span>
            </div>

            {/* Table */}
            <table className="loadboard-table">
              <thead>
                <tr className="bg-[#162A4A]">
                  <th>Origin</th>
                  <th></th>
                  <th>Destination</th>
                  <th>Vehicle</th>
                  <th>Price</th>
                  <th>Age</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sampleLoads.map((load, i) => (
                  <tr key={i}>
                    <td className="text-white font-medium">{load.origin}</td>
                    <td className="text-[#60A5FA]"><ArrowRight className="w-4 h-4" /></td>
                    <td className="text-white font-medium">{load.dest}</td>
                    <td className="text-gray-400">{load.vehicle}</td>
                    <td className="text-[#4ADE80] font-bold">${load.price.toLocaleString()}</td>
                    <td className="text-gray-500">{load.age}</td>
                    <td>
                      {load.status === 'verified' ? (
                        <span className="status-verified px-3 py-1 rounded-md text-xs flex items-center gap-1.5 w-fit">
                          <CheckCircle className="w-3 h-3" />
                          Verified
                        </span>
                      ) : (
                        <span className="status-assigned px-3 py-1 rounded-md text-xs">Assigned</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-[#0B1E33]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-white mb-3" style={{fontFamily: 'Poppins, sans-serif'}}>
              Why Choose <span className="text-[#4ADE80]">HaulLogix</span>
            </h2>
            <p className="text-[#94A3B8] text-lg">
              Built for transport professionals who value security and efficiency.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="bg-[#112240] border border-[#3B82F6]/20 rounded-xl p-6 animate-fade-in hover:border-[#3B82F6]/40 transition-all hover:-translate-y-1"
                style={{ animationDelay: `${index * 100}ms`, boxShadow: '0 4px 24px rgba(59, 130, 246, 0.1)' }}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3B82F6]/20 to-[#4ADE80]/20 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#60A5FA]" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">{feature.title}</h3>
                <p className="text-sm text-[#94A3B8]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for Trust Section */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-[#3B82F6]/15 text-[#60A5FA] px-5 py-2.5 rounded-full text-sm font-medium mb-5 border border-[#3B82F6]/30">
              <Shield className="w-4 h-4" />
              Fraud Shield Technology
            </div>
            <h2 className="text-3xl font-bold text-white mb-3" style={{fontFamily: 'Poppins, sans-serif'}}>
              Built for <span className="text-[#4ADE80]">Trust</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
              Our multi-layered security system protects every transaction on the platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Verified Accounts */}
            <div className="bg-gradient-to-br from-[#162A4A] to-[#112240] border border-[#3B82F6]/20 rounded-xl p-6 group hover:border-[#4ADE80]/40 transition-all" style={{boxShadow: '0 4px 24px rgba(59, 130, 246, 0.1)'}}>
              <div className="w-14 h-14 rounded-xl bg-[#4ADE80]/15 flex items-center justify-center mb-5 group-hover:bg-[#4ADE80]/25 transition-colors">
                <UserCheck className="w-7 h-7 text-[#4ADE80]" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">Verified Accounts</h3>
              <p className="text-sm text-[#94A3B8]">
                Every carrier, broker, and shipper goes through our verification process.
              </p>
            </div>

            {/* Fraud Reporting */}
            <div className="bg-gradient-to-br from-[#162A4A] to-[#112240] border border-[#3B82F6]/20 rounded-xl p-6 group hover:border-[#EF4444]/40 transition-all" style={{boxShadow: '0 4px 24px rgba(59, 130, 246, 0.1)'}}>
              <div className="w-14 h-14 rounded-xl bg-[#EF4444]/15 flex items-center justify-center mb-5 group-hover:bg-[#EF4444]/25 transition-colors">
                <AlertTriangle className="w-7 h-7 text-[#EF4444]" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">Fraud Reporting</h3>
              <p className="text-sm text-[#94A3B8]">
                Community-powered fraud detection. Report suspicious activity and protect fellow professionals.
              </p>
            </div>

            {/* Admin Review */}
            <div className="bg-gradient-to-br from-[#162A4A] to-[#112240] border border-[#3B82F6]/20 rounded-xl p-6 group hover:border-[#3B82F6]/40 transition-all" style={{boxShadow: '0 4px 24px rgba(59, 130, 246, 0.1)'}}>
              <div className="w-14 h-14 rounded-xl bg-[#3B82F6]/15 flex items-center justify-center mb-5 group-hover:bg-[#3B82F6]/25 transition-colors">
                <ShieldCheck className="w-7 h-7 text-[#60A5FA]" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">Admin Review</h3>
              <p className="text-sm text-[#94A3B8]">
                Our security team reviews flagged accounts and takes action to maintain platform integrity.
              </p>
            </div>

            {/* Secure Load Visibility */}
            <div className="bg-gradient-to-br from-[#162A4A] to-[#112240] border border-[#3B82F6]/20 rounded-xl p-6 group hover:border-[#22D3EE]/40 transition-all" style={{boxShadow: '0 4px 24px rgba(59, 130, 246, 0.1)'}}>
              <div className="w-14 h-14 rounded-xl bg-[#22D3EE]/15 flex items-center justify-center mb-5 group-hover:bg-[#22D3EE]/25 transition-colors">
                <Eye className="w-7 h-7 text-[#22D3EE]" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">Load Visibility</h3>
              <p className="text-sm text-[#94A3B8]">
                Control who sees your loads. Verified Load Chain tracks every interaction.
              </p>
            </div>
          </div>

          {/* Trust Badge */}
          <div className="mt-10 text-center">
            <div className="inline-flex items-center gap-2.5 text-sm text-[#94A3B8] bg-[#112240] px-5 py-2.5 rounded-full border border-[#3B82F6]/20">
              <Shield className="w-4 h-4 text-[#4ADE80]" />
              <span>If it's not verified, it's not HaulLogix</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{fontFamily: 'Poppins, sans-serif'}}>
            Ready to Get <span className="text-[#4ADE80]">Started</span>?
          </h2>
          <p className="text-[#94A3B8] text-lg mb-10">
            Join thousands of verified transport professionals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-[#4ADE80] to-[#22C55E] hover:from-[#86EFAC] hover:to-[#4ADE80] text-black font-semibold h-14 px-12 text-base"
                style={{boxShadow: '0 6px 24px rgba(74, 222, 128, 0.4)'}}
                data-testid="cta-create-account-btn"
              >
                Create Free Account
              </Button>
            </Link>
            <Link to="/pricing">
              <Button 
                size="lg" 
                variant="outline"
                className="h-14 px-12 text-base border-[#3B82F6] bg-[#1E3A8A]/40 text-white hover:bg-[#3B82F6]/30 font-medium"
                style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.25)'}}
                data-testid="cta-view-pricing-btn"
              >
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
